/**
 * Builder codes (#770) with no React in it. Research + testnet proof: docs/research/BUILDER_CODES.md.
 *
 * Hyperliquid builder codes let a frontend charge a fee on the orders it sends:
 *   1. the user's WALLET signs a user-signed `approveBuilderFee {maxFeeRate: "0.01%", builder, nonce}` once (an
 *      agent key cannot sign it); /info {type:"maxBuilderFee", user, builder} then returns the approved maximum in
 *      tenths of a basis point;
 *   2. every order action carries `builder: {b: <lowercase address>, f: <fee in tenths of a bp>}` (f <= approved);
 *      Hyperliquid charges the fee with the trade and credits it to the builder.
 *
 * DISABLED BY DEFAULT: with no builder address configured, no order carries a `builder` key, no approval is asked
 * for and no fee is shown. Configure at build time (Vite env), per network if needed:
 *   VITE_BUILDER_ADDRESS=0x…            builder address (both networks)
 *   VITE_BUILDER_FEE=10                 fee in tenths of a basis point (10 = 1 bp = 0.01%); 1..MAX_BUILDER_FEE
 *   VITE_BUILDER_ADDRESS_TESTNET / _MAINNET, VITE_BUILDER_FEE_TESTNET / _MAINNET   per-network overrides
 * e2e test seam: `window.__hip4Builder = { address, fee }` before the app loads (applies to both networks).
 */
import type { Hex } from 'viem';
import type { EnvConfig, EnvName } from '../config/env';
import { postExchange, signApproveBuilderFeeOnCurrentChain, type Eip1193Provider } from '../hl/exchange';
import { gatedInfoFetch } from '../hl/healthMonitor';
import type { BuilderInfo } from '../hl/signing';
import { cmp, decToString, mul, parseDec, roundDec, ZERO } from '../trading/decimal';

/** Hyperliquid's cap for spot (outcome) builder fees: 1% = 100 bp = 1000 tenths of a bp. */
export const MAX_BUILDER_FEE = 1000;

export interface BuilderConfig {
  /** Lowercase builder address (the form the order's `b` must have). */
  address: Hex;
  /** Fee per order in tenths of a basis point (`f`), 1..MAX_BUILDER_FEE. */
  fee: number;
}

export interface RawBuilderSettings {
  address?: string | null;
  fee?: string | number | null;
}

/** Validated config, or null (= builder codes off) for a missing/invalid address or a fee outside 1..1000. */
export function parseBuilderConfig(raw: RawBuilderSettings | null | undefined): BuilderConfig | null {
  const address = typeof raw?.address === 'string' ? raw.address.trim() : '';
  if (!/^0x[0-9a-fA-F]{40}$/.test(address) || /^0x0{40}$/.test(address)) return null;
  const f = typeof raw?.fee === 'number' ? raw.fee : typeof raw?.fee === 'string' && raw.fee.trim() !== '' ? Number(raw.fee) : NaN;
  if (!Number.isSafeInteger(f) || f < 1 || f > MAX_BUILDER_FEE) return null;
  return { address: address.toLowerCase() as Hex, fee: f };
}

type ViteEnv = Record<string, string | undefined>;

const OVERRIDE_KEY = '__hip4Builder';

function runtimeOverride(): RawBuilderSettings | null {
  try {
    const w = typeof window !== 'undefined' ? (window as unknown as Record<string, unknown>) : null;
    const o = w?.[OVERRIDE_KEY];
    return o && typeof o === 'object' ? (o as RawBuilderSettings) : null;
  } catch {
    return null;
  }
}

/**
 * The builder config for a network: the e2e seam, else the per-network Vite variables, else the shared ones. Null when
 * builder codes are off (the default).
 */
export function builderConfigFor(
  name: EnvName,
  viteEnv: ViteEnv = (import.meta.env ?? {}) as unknown as ViteEnv,
  override: RawBuilderSettings | null = runtimeOverride(),
): BuilderConfig | null {
  if (override) return parseBuilderConfig(override);
  const suffix = name === 'mainnet' ? '_MAINNET' : '_TESTNET';
  return parseBuilderConfig({
    address: viteEnv[`VITE_BUILDER_ADDRESS${suffix}`] || viteEnv.VITE_BUILDER_ADDRESS,
    fee: viteEnv[`VITE_BUILDER_FEE${suffix}`] || viteEnv.VITE_BUILDER_FEE,
  });
}

/** The order action's `builder` field, or null when off. */
export function builderField(cfg: BuilderConfig | null): BuilderInfo | null {
  return cfg ? { b: cfg.address, f: cfg.fee } : null;
}

/** Fee as a fraction of the order value: tenths of a bp / 100000 (10 -> "0.0001"). */
export function feeToRate(f: number): string {
  return decToString({ n: BigInt(f), s: 5 });
}

/** Fee as a percent string without the sign (10 -> "0.01", 1000 -> "1"). */
export function feeToPercent(f: number): string {
  return decToString({ n: BigInt(f), s: 3 });
}

/** approveBuilderFee `maxFeeRate` for a fee: "0.01%". */
export function maxFeeRateFor(f: number): string {
  return `${feeToPercent(f)}%`;
}

/**
 * Builder fee in USDC for an order of `value` USDC (price * shares), rounded UP to 8 decimals (an upper bound for the
 * ticket; Hyperliquid computes it on the actual fill). "0" for junk input.
 *
 * Charged on BUYS AND SELLS of outcomes: measured on testnet (#770 proof, docs/research/BUILDER_CODES.md), f=100
 * charged builderFee 0.00128 on a 1.28 USDC buy and 0.0012 on a 1.2 USDC sell, exactly value * f / 100000. (The
 * Hyperliquid docs say spot builder fees apply only to sells; for outcomes they do not.) Settlement is not an order
 * and carries no builder fee.
 */
export function builderFeeUsd(value: string | null | undefined, f: number): string {
  if (!Number.isSafeInteger(f) || f <= 0) return '0';
  return builderFeeAtRate(value, feeToRate(f));
}

/**
 * The builder fee maths, shared by the order ticket (builderFeeUsd, fee in tenths of a bp) and the Learn page fee model
 * (trading/fees.ts outcomeFee, fee as a fraction): `value * rate` rounded UP to 8 decimals, the same on buys and sells.
 * "0" for a missing, junk, zero or negative value or rate.
 */
export function builderFeeAtRate(value: string | null | undefined, rate: string | null | undefined): string {
  const v = value ? parseDec(value) : null;
  const r = rate ? parseDec(rate) : null;
  if (!v || !r || cmp(v, ZERO) <= 0 || cmp(r, ZERO) <= 0) return '0';
  return decToString(roundDec(mul(v, r), 8, 'up'));
}

/** "0xae92…eb1a". */
export function shortAddress(a: string): string {
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

/** A small USDC fee for display: "$0.00012", "$0.0123", "$1.50" (rounded up to 6 decimals, at least 2). */
export function formatFeeUsd(fee: string): string {
  const d = parseDec(fee);
  if (!d || cmp(d, ZERO) <= 0) return '$0.00';
  const s = decToString(roundDec(d, 6, 'up'));
  const [i, f = ''] = s.split('.');
  return `$${i}.${f.padEnd(2, '0')}`;
}

/** POST /info {type:"maxBuilderFee", user, builder}: the approved maximum in tenths of a bp (0 = none). */
export async function fetchMaxBuilderFee(
  env: Pick<EnvConfig, 'infoUrl'>,
  user: string,
  builder: string,
  fetchImpl: typeof fetch = fetch,
): Promise<number> {
  const res = await gatedInfoFetch(env.infoUrl, fetchImpl)(env.infoUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type: 'maxBuilderFee', user: user.toLowerCase(), builder: builder.toLowerCase() }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status} from /info maxBuilderFee`);
  const body: unknown = await res.json();
  const n = typeof body === 'number' ? body : typeof body === 'string' ? Number(body) : NaN;
  if (!Number.isSafeInteger(n) || n < 0) throw new Error(`unexpected maxBuilderFee response ${JSON.stringify(body)}`);
  return n;
}

/**
 * Approval state for the ticket:
 *   'off'       builder codes are not configured (orders carry no builder field);
 *   'checking'  waiting for /info maxBuilderFee;
 *   'approved'  the approved maximum covers the fee;
 *   'needed'    the user must sign approveBuilderFee before the first order;
 *   'unknown'   /info failed: orders are still allowed (Hyperliquid rejects them if the approval is missing).
 */
export type BuilderApproval = 'off' | 'checking' | 'approved' | 'needed' | 'unknown';

export function approvalFor(cfg: BuilderConfig | null, maxApproved: number | null | undefined, failed = false): BuilderApproval {
  if (!cfg) return 'off';
  if (failed) return 'unknown';
  if (maxApproved === null || maxApproved === undefined) return 'checking';
  return maxApproved >= cfg.fee ? 'approved' : 'needed';
}

/** Hyperliquid's rejection for an order whose builder fee was never approved (or is above the approved maximum). */
export function isBuilderApprovalError(message: string): boolean {
  return /builder/i.test(message) && /(approv|exceed|max)/i.test(message);
}

/** approveBuilderFee was refused by /exchange. */
export class BuilderApproveError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BuilderApproveError';
  }
}

export interface ApproveDeps {
  provider: Eip1193Provider;
  account: Hex;
  env: EnvConfig;
  cfg: BuilderConfig;
  now?: () => number;
  fetchImpl?: typeof fetch;
}

/**
 * The wallet signs approveBuilderFee for exactly the configured fee (on its current chain, no network switch), then
 * POST /exchange and re-read maxBuilderFee. Wallet errors (user rejection etc.) propagate unchanged; an /exchange
 * refusal throws BuilderApproveError. Returns the approved maximum Hyperliquid now reports.
 */
export async function approveBuilderFee(d: ApproveDeps): Promise<number> {
  const nonce = (d.now ?? Date.now)();
  const { payload } = await signApproveBuilderFeeOnCurrentChain(
    d.provider,
    d.account,
    d.cfg.address,
    maxFeeRateFor(d.cfg.fee),
    nonce,
    d.env,
  );
  let resp;
  try {
    resp = await postExchange(d.env, payload, d.fetchImpl);
  } catch (e) {
    throw new BuilderApproveError(e instanceof Error ? e.message : String(e));
  }
  if (resp.status !== 'ok') {
    throw new BuilderApproveError(typeof resp.response === 'string' ? resp.response : JSON.stringify(resp.response));
  }
  try {
    return await fetchMaxBuilderFee(d.env, d.account, d.cfg.address, d.fetchImpl);
  } catch {
    return d.cfg.fee; // accepted; /info will catch up on the next check
  }
}
